/**
 * The vLLM settings form, drawn from the backend's schema.
 *
 * Exists so `--max-model-len 4096` stops being free text somebody has to know
 * by heart. A typo in a flag name is not a wrong answer discovered later — it
 * is a hard argparse failure several minutes into a start, naming a flag the
 * operator never meant to type.
 *
 * Two things are deliberate and easy to lose in a tidy-up:
 *
 *   Empty means default. Every field starts blank and a blank field sends
 *   nothing, so a form somebody opened and closed produces exactly the command
 *   line they would have got without opening it. A form that quietly writes its
 *   own defaults into the command is a form that changes behaviour by being
 *   looked at.
 *
 *   It says these need a restart. Unlike Ollama, where a setting is baked into
 *   a derived model and sticks, every one of these is a launch argument. The
 *   note is the backend's own words, so it cannot drift from the truth.
 */
import { useState } from "react";

import type { VllmField, VllmSchema, VllmSettings } from "./api";

export function SettingsFields({
  schema,
  values,
  onChange,
}: {
  schema: VllmSchema;
  values: VllmSettings;
  onChange: (next: VllmSettings) => void;
}) {
  // Collapsed by default. Most starts need none of this, and a wall of nine
  // fields in front of somebody who wants to type a model name and press Start
  // is a worse first impression than a link.
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (key: string, value: string | number | boolean | null) =>
    onChange({ ...values, [key]: value });

  const groups = [...schema.groups].sort((a, b) => a.order - b.order);
  const advancedCount = schema.fields.filter((f) => f.advanced).length;

  // Anything that is not already the default. Zero means the form is at rest,
  // which is what "Reset" checks so it can sit disabled rather than pretend to
  // do something.
  const changed = Object.keys(chosen(values)).filter(
    (k) => values[k] !== defaults(schema)[k],
  ).length;

  return (
    <div className="vsettings">
      {/* What this form was drawn for. A form that looks the same for every
          model invites treating it as one, and it is not — the presets, the
          precision and the pre-ticked switch below all came from this. */}
      {schema.facts.length > 0 && (
        <p className="vfacts">{schema.facts.join(" · ")}</p>
      )}
      <p className="lede">{schema.note}</p>

      {groups.map((group) => {
        const fields = schema.fields.filter(
          (f) => f.group === group.key && (showAdvanced || !f.advanced),
        );
        if (fields.length === 0) return null;
        return (
          <section key={group.key}>
            <h4>{group.label}</h4>
            <div className="vgrid">
              {fields.map((field) => (
                <Field
                  key={field.key}
                  field={field}
                  value={values[field.key] ?? null}
                  onChange={(v) => set(field.key, v)}
                />
              ))}
            </div>
          </section>
        );
      })}

      <div className="vfoot">
        <button
          className="linkish"
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced
            ? "Hide advanced"
            : `Show ${advancedCount} advanced settings`}
        </button>
        {/* Back to the model's own defaults — not to an empty form, because for
            a model that needs its own code "default" includes that switch on,
            and clearing it would produce a start that fails. */}
        <button
          className="linkish"
          type="button"
          disabled={changed === 0}
          onClick={() => onChange(defaults(schema))}
        >
          Reset to the model&rsquo;s defaults
        </button>
      </div>
    </div>
  );
}

function Field({
  field,
  value,
  onChange,
}: {
  field: VllmField;
  value: string | number | boolean | null;
  onChange: (value: string | number | boolean | null) => void;
}) {
  if (field.type === "bool") {
    return (
      <label className="vcheck">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          {field.label}
          {field.help && <em>{field.help}</em>}
        </span>
      </label>
    );
  }

  return (
    <label className="vfield">
      <span className="vlabel">
        {field.label}
        {field.unit && <span className="unit"> {field.unit}</span>}
        {/* The flag itself, because someone reading this is often holding a
            vLLM doc page open in the next tab, and matching the two up by
            guesswork is the thing this form removes. */}
        <code className="vflag">{field.flag}</code>
      </span>

      {field.type === "enum" ? (
        <select
          value={value === null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value || null)}
        >
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <>
          <input
            type="number"
            value={value === null ? "" : String(value)}
            placeholder="vLLM's default"
            min={field.min ?? undefined}
            max={field.max ?? undefined}
            step={field.type === "float" ? 0.05 : 1}
            onChange={(e) =>
              onChange(e.target.value === "" ? null : Number(e.target.value))
            }
          />
          {field.presets && (
            <div className="vpresets">
              {field.presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={value === p ? "on" : ""}
                  onClick={() => onChange(value === p ? null : p)}
                >
                  {p >= 1024 ? `${p / 1024}k` : p}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {field.help && <span className="vhelp">{field.help}</span>}
    </label>
  );
}

/**
 * The model's own starting point: every field blank except the ones the
 * schema pre-sets — today that is only the remote-code switch, ticked for a
 * model that will not load without it. Blank means "vLLM's default", so this
 * is the same command line as no form at all, plus whatever the model needs.
 */
export function defaults(schema: VllmSchema): VllmSettings {
  return Object.fromEntries(
    schema.fields
      .filter((f) => f.type === "bool" && f.default === true)
      .map((f) => [f.key, true]),
  );
}

/** Drop the blanks. What reaches the backend is only what someone actually
 *  chose, so an untouched form is indistinguishable from no form. */
export function chosen(values: VllmSettings): VllmSettings {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([, v]) => v !== null && v !== "" && v !== false,
    ),
  );
}
